import {
  ResourceView,
  type FilterDefinition,
  type FilterMenuOutput,
} from "@/shared/components/ResourceView";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { functionalUpdate } from "@/shared/foundation/primitives/Table";

import styles from "./OverviewFilterBar.module.css";
import type { OverviewFilters } from "./overviewUrl";

interface OverviewFilterBarProps {
  filters: OverviewFilters;
  /**
   * The Sessions tab's filter definitions, so a filter matches the same
   * sessions on both tabs.
   */
  definitions: FilterDefinition[];
  /** Takes an updater applied to the latest filters; the page stores the result in the URL. */
  onChange: (update: (previous: OverviewFilters) => OverviewFilters) => void;
}

/**
 * Renders the Filter button and a chip per active filter, matching the
 * Sessions tab. Holds no state: the filters live in the URL, so every add,
 * edit, removal, or match-mode switch goes through `onChange`.
 */
export function OverviewFilterBar({
  filters,
  definitions,
  onChange,
}: OverviewFilterBarProps) {
  const metricsEnabled = useCheckFeatureFlag("release-metrics");

  // The menu gets no RoboQL config, so it only ever emits structured filters.
  const add = (output: FilterMenuOutput) => {
    if (output.type !== "structured") {
      return;
    }
    onChange((previous) => ({
      ...previous,
      filters: [...previous.filters, output.filter],
    }));
  };

  return (
    <div className={styles.filterBar}>
      <div className={styles.header}>
        <h2 className={styles.title}>All sessions</h2>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={definitions}
            onFilterSelected={add}
            metrics={metricsEnabled}
          />
        </ResourceView.Actions>
      </div>
      <ResourceView.ActiveFilters
        filters={
          filters.filters.length > 0
            ? { type: "structured", ...filters }
            : { type: "none" }
        }
        filterDefinitions={definitions}
        onFilterSelected={add}
        onRemove={(index) =>
          onChange((previous) => ({
            ...previous,
            filters: previous.filters.filter((_, i) => i !== index),
          }))
        }
        onUpdate={(index, filter) =>
          onChange((previous) => ({
            ...previous,
            filters: previous.filters.map((existing, i) =>
              i === index ? filter : existing,
            ),
          }))
        }
        onClear={() => onChange((previous) => ({ ...previous, filters: [] }))}
        onMatchModeChange={(updater) =>
          onChange((previous) => ({
            ...previous,
            matchMode: functionalUpdate(updater, previous.matchMode),
          }))
        }
        metrics={metricsEnabled}
      />
    </div>
  );
}
